/**
 * @author pkleim on 3/31/16
 *
 * @fileOverview Renders a LoginLinks Component, the secondary links on Login component
 * @module LoginLinks
 * @requires React
 * @requires @americanexpress/dls-react/Anchor
 *
 * @param {object} language - language object associated with current locale;
 *                            Usually obtained from the store*
 * @example
 * import { LoginLinks } from 'one-identity-login';
 * <LoginLinks menuKey="account" />
 *
 */
import React, { memo } from 'react';
import { string, shape, arrayOf } from 'prop-types';
import { Link } from '@americanexpress/one-app-router';
import { Anchor } from '@americanexpress/dls-react';
import { useReactlytics } from '@americanexpress/reactlytics';
import { qualifyUrl } from '../utils';

const LoginLinks = memo(({
  destination, links, location, align,
}) => {
  const { track } = useReactlytics();

  const onClick = (label) => {
    track({
      event: {
        element: 'Login_Link',
        detail: label,
        type: 'click',
      },
    });
  };

  return (
    <ul className={`list-links margin-2-t text-align-${align}`}>
      {links.map((link) => {
        const {
          label,
          key,
          overrideKey,
          url,
          title,
          ...others
        } = link;

        if (destination && global.BROWSER) {
          let href = url;
          let source = destination.url;
          const isExternal = href.startsWith('http');
          if (isExternal) {
            source = encodeURIComponent(qualifyUrl(decodeURIComponent(source), location));
          }
          href = href.replace('{dest}', source);

          return (
            <li key={`${key}-${overrideKey}`}>
              {isExternal ? (
                <Anchor
                // eslint-disable-next-line react/jsx-props-no-spreading -- allowing jsx-props-no-spreading
                  {...others}
                  data-testid={`${key}-link`}
                  href={href}
                  title={title}
                  label={label}
                  theme={{ textDecoration: 'none' }}
                  inline={true}
                  className="text-link"
                  reactlytics={{
                    element: 'Login_Link',
                    detail: label,
                  }}
                >
                  {label}
                </Anchor>
              ) : (
                <Link
                  data-testid={`${key}-link`}
                  to={href}
                  className="text-link"
                  onClick={() => onClick(label)}
                >
                  <span>
                    {label}
                  </span>
                </Link>
              )}
            </li>
          );
        }

        return (
          <li key={`${key}-${overrideKey}`}>
            <span className="dls-bright-blue">{label}</span>
          </li>
        );
      })}
    </ul>
  );
});

LoginLinks.propTypes = {
  destination: shape({
    url: string,
    overrideKey: string,
  }).isRequired,
  links: arrayOf(shape({})),
  location: shape({
    host: string,
    protocol: string,
  }).isRequired,
  align: string,
};

LoginLinks.defaultProps = {
  links: [],
  align: 'left',
};

LoginLinks.displayName = 'LoginLinks';

export default LoginLinks;
